'use client';

import { ReactNode } from 'react';

interface PageTransitionProps {
  children: ReactNode;
  className?: string;
}

// Type-safe animation class names
type AnimationClass = 
  | 'animate-fade-in'
  | 'animate-slide-up'
  | 'animate-slide-in-left'
  | 'animate-scale-in'
  | 'animate-stagger';

// Type-safe delay class names
type DelayClass = 
  | 'animation-delay-100'
  | 'animation-delay-200'
  | 'animation-delay-300'
  | 'animation-delay-400'
  | 'animation-delay-500'
  | 'animation-delay-600'
  | 'animation-delay-700'
  | 'animation-delay-800'
  | 'animation-delay-900'
  | 'animation-delay-1000';

export default function PageTransition({ children, className = '' }: PageTransitionProps) {
  // Sanitize className to prevent potential issues
  const sanitizedClassName = typeof className === 'string' ? className.trim() : '';
  
  return (
    <div className={`animate-fade-in ${sanitizedClassName}`}>
      {children}
    </div>
  );
}

// CSS animation classes for consistent animations
export const animationClasses: Record<string, AnimationClass | string> = {
  // Fade in animation
  fadeIn: 'animate-fade-in',
  
  // Slide up animation
  slideUp: 'animate-slide-up',
  
  // Slide in from left
  slideInLeft: 'animate-slide-in-left',
  
  // Scale in animation
  scaleIn: 'animate-scale-in',
  
  // Stagger animation for children
  stagger: 'animate-stagger',
  
  // Hover animations
  hoverScale: 'hover:scale-105 transition-transform duration-300',
  hoverLift: 'hover:-translate-y-1 transition-transform duration-300',
  
  // Button animations
  buttonHover: 'hover:scale-105 active:scale-95 transition-transform duration-200',
} as const;

// Animation delay utilities - reduced to commonly used delays
export const animationDelays: Record<string, DelayClass> = {
  delay100: 'animation-delay-100',
  delay200: 'animation-delay-200',
  delay300: 'animation-delay-300',
  delay400: 'animation-delay-400',
  delay500: 'animation-delay-500',
  delay600: 'animation-delay-600',
  delay700: 'animation-delay-700',
  delay800: 'animation-delay-800',
  delay900: 'animation-delay-900',
  delay1000: 'animation-delay-1000',
} as const; 